import { useCallback } from "react";

import { type FetchResult } from "@apollo/client";
import { type MylensesSubmissionDetailsSource } from "@snapchat/blizzard-schema";

import { useBlizzardEvents } from "../../analytics/hooks";
import { type MyLensesLensCta } from "../../gql-generated/types";
import { type SetLensCtaMutation } from "../../lens/api/mutations";
import { useSetLensCta } from "../../lens/hooks";
import { type ModalProps, type ShowModal, useToggleState } from "../../shared/hooks";
import { lensCtaAtomById } from "../../shared/state";
import { makeLensCtaToSubmit } from "../models";

import { useLensCta } from "./useLensCta";

interface UseLensCtaFormProps {
    lensOrSubmissionId: string;
    createChangeDetailsEvent?: (source: MylensesSubmissionDetailsSource) => () => void;
    isDetailsPage?: boolean;
}

export const useLensCtaForm = ({
    createChangeDetailsEvent,
    isDetailsPage,
    lensOrSubmissionId,
}: UseLensCtaFormProps): {
    lensCta: MyLensesLensCta | null;
    lensCtaModalProps: ModalProps;
    sendLensCtaLoading: boolean;
    applyLensCta: (lensCta: MyLensesLensCta) => void;
    sendLensCta: (lensCta: MyLensesLensCta) => Promise<FetchResult<SetLensCtaMutation>>;
    showLensCtaModal: ShowModal;
} => {
    const [lensCtaModalProps, showLensCtaModal] = useToggleState();
    const { lensCta, setLensCta } = useLensCta(lensCtaAtomById(lensOrSubmissionId));
    const { sendBlizzardEvent } = useBlizzardEvents();

    // mutation on the details page
    const { sendLensCta, loading: sendLensCtaLoading } = useSetLensCta();

    const applyLensCta = useCallback(
        (lensCta: MyLensesLensCta) => {
            // set Lens Cta on the form

            setLensCta(lensCta);
            if (isDetailsPage) {
                const lensCtaToSubmit = makeLensCtaToSubmit({
                    text: lensCta.text,
                    type: lensCta.type,
                    website: { url: lensCta.website?.url },
                    deeplink: {
                        androidAppUrl: lensCta.deeplink?.androidAppUrl,
                        appName: lensCta.deeplink?.appName,
                        iosAppId: lensCta.deeplink?.iosAppId,
                        uri: lensCta.deeplink?.uri,
                    },
                });
                sendLensCta(lensCtaToSubmit)
                    .then(() => {
                        sendBlizzardEvent({
                            event: "MYLENSES_MANAGEMENT_PAGE_ACTION",
                            pageType: "LENS_DETAILS",
                            action: "EDIT_CTA",
                        });
                    })
                    .catch(() => {
                        // TODO - Error handling
                    });
            } else {
                createChangeDetailsEvent?.("CTA")();
            }
            lensCtaModalProps.onCancel();
        },
        [isDetailsPage, lensCtaModalProps, setLensCta, sendLensCta, createChangeDetailsEvent, sendBlizzardEvent],
    );

    return {
        lensCta,
        lensCtaModalProps,
        sendLensCtaLoading,
        applyLensCta,
        sendLensCta,
        showLensCtaModal,
    };
};
